import { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { supabase } from '../lib/supabase';
import { OnboardingForm, OnboardingData } from './OnboardingForm';
import { AlertCircle, Loader2, LogOut } from 'lucide-react';

interface OnboardingManagerProps {
  onComplete: () => void;
}

interface OnboardingStatus {
  isOnboardingComplete: boolean;
  isLoading: boolean;
  error: string | null;
}

export function OnboardingManager({ onComplete }: OnboardingManagerProps) {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus>({
    isOnboardingComplete: false,
    isLoading: true,
    error: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Check if user has completed onboarding
  const checkOnboardingStatus = async () => {
    if (!user || !isLoaded) return;

    try {
      setOnboardingStatus(prev => ({ ...prev, isLoading: true, error: null }));

      const email = user.emailAddresses[0]?.emailAddress;
      if (!email) {
        throw new Error('No email address found');
      }

      // Check if user profile exists and has a role other than 'Viewer' (default)
      const { data: profile, error } = await supabase.rpc('get_user_profile_by_email', {
        p_email: email
      });

      if (error) {
        throw new Error(error.message);
      }

      // If profile exists and has been customized (not just default Viewer), onboarding is complete
      const userProfile = profile && profile.length > 0 ? profile[0] : null;
      const isComplete = userProfile && 
        userProfile.role !== 'Viewer' && 
        userProfile.first_name && 
        userProfile.last_name;

      setOnboardingStatus({
        isOnboardingComplete: isComplete,
        isLoading: false,
        error: null
      });

      if (isComplete) {
        onComplete();
      }
    } catch (err) {
      console.error('Error checking onboarding status:', err);
      setOnboardingStatus({
        isOnboardingComplete: false,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to check onboarding status'
      });
    }
  };

  // Handle onboarding form submission
  const handleOnboardingSubmit = async (data: OnboardingData) => {
    if (!user) {
      setSubmitError('User not found');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const email = user.emailAddresses[0]?.emailAddress;
      if (!email) {
        throw new Error('No email address found');
      }

      // Call the onboarding function to update user role and profile
      const { data: result, error } = await supabase.rpc('update_user_role_onboarding', {
        p_email: email,
        p_new_role: data.role,
        p_first_name: data.firstName,
        p_last_name: data.lastName
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!result) {
        throw new Error('Failed to update user profile');
      }

      // Store additional onboarding data in metadata and mark onboarding as completed
      const { error: metadataError } = await supabase.rpc('update_user_metadata', {
        p_email: email,
        p_metadata: {
          company: data.company,
          experience: data.experience,
          interests: data.interests,
          onboarding_completed_at: new Date().toISOString()
        }
      });

      if (metadataError) {
        console.warn('Failed to save additional onboarding data:', metadataError);
        // Don't throw here as the main onboarding is complete
      }

      // Mark onboarding as complete
      setOnboardingStatus({
        isOnboardingComplete: true,
        isLoading: false,
        error: null
      });

      // Call the completion callback
      onComplete();
    } catch (err) {
      console.error('Error submitting onboarding:', err);
      setSubmitError(err instanceof Error ? err.message : 'Failed to complete onboarding');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isLoaded && user) {
      checkOnboardingStatus();
    }
  }, [isLoaded, user]);

  // Show loading state while checking onboarding status
  if (onboardingStatus.isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Checking your account...</p>
        </div>
      </div>
    );
  }

  // Show error state if there's an error checking onboarding status
  if (onboardingStatus.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">{onboardingStatus.error}</p>
          <button
            onClick={checkOnboardingStatus}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If onboarding is complete, don't show anything (parent will handle navigation)
  if (onboardingStatus.isOnboardingComplete) {
    return null;
  }

  // Show onboarding form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="text-center flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Welcome to Financial Dashboard</h1>
              <p className="text-gray-600 mt-1">Let's set up your account to get started</p>
            </div>
            {/* Temporary logout button for testing */}
            <button
              onClick={() => signOut()}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <p className="text-red-700">{submitError}</p>
              </div>
            </div>
          )}

          <OnboardingForm 
            onSubmit={handleOnboardingSubmit}
            isLoading={isSubmitting}
          />

          {/* Help text */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Need help? Contact support at{' '}
              <a href="mailto:support@example.com" className="text-blue-600 hover:text-blue-700">
                support@example.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to check if user needs onboarding
export function useOnboardingStatus() {
  const { user, isLoaded } = useUser();
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Don't run onboarding check if user is not loaded or not signed in
    if (!isLoaded || !user) {
      setNeedsOnboarding(false);
      setIsLoading(false);
      return;
    }

    const checkStatus = async () => {
      // Always start with loading state
      setIsLoading(true);
      setNeedsOnboarding(null);

      try {
        const email = user.emailAddresses[0]?.emailAddress;
        if (!email) {
          setNeedsOnboarding(false);
          setIsLoading(false);
          return;
        }

        // Check if this is a new signup (user created within last 10 minutes)
        const userCreatedAt = user.createdAt ? new Date(user.createdAt) : new Date();
        const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
        const isNewSignup = userCreatedAt > tenMinutesAgo;
        

        const { data: profile, error } = await supabase.rpc('get_user_profile_by_email', {
          p_email: email
        });

        if (error) {
          console.error('Error checking onboarding status:', error);
          setNeedsOnboarding(false);
        } else if (!profile || profile.length === 0) {
          // No profile exists - this is definitely a new user who needs onboarding
          setNeedsOnboarding(true);
        } else {
          // Check if onboarding has been completed (stored in metadata)
          const userProfile = profile[0]; // Get the first (and only) profile
          const hasCompletedOnboarding = userProfile.metadata?.onboarding_completed_at;
          
          if (hasCompletedOnboarding) {
            // User has completed onboarding, don't show it again
            console.log('User has completed onboarding, skipping');
            setNeedsOnboarding(false);
          } else if (isNewSignup) {
            // This is a new signup - show onboarding regardless of profile state
            console.log('New signup detected, showing onboarding');
            setNeedsOnboarding(true);
          } else {
            // Existing user with incomplete profile - don't show onboarding
            // (they can complete their profile through other means)
            console.log('Existing user, not showing onboarding');
            setNeedsOnboarding(false);
          }
        }
      } catch (err) {
        console.error('Error checking onboarding status:', err);
        setNeedsOnboarding(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkStatus();
  }, [user, isLoaded]);

  return { needsOnboarding, isLoading };
}
